// src/Admin/NotificationsPage.js
/* =========================================================
   /Admin/notifications — every admin notification on one page,
   full text (nothing cut), grouped by day, with filters.
   Opened from the bell's "See all notifications".
   ========================================================= */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCheckDouble, faMagnifyingGlass, faArrowRight, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { TYPE_META, ago } from './notificationMeta';
import './NotificationsPage.css';

const API = process.env.REACT_APP_API_BASE_URL;
const BASE = `${API}/api/admin/notifications`;
const PAGE = 50;
const TZ = 'Asia/Beirut';

const TYPE_LABELS = {
  demo_request: 'Demo requests',
  student_joined: 'Student joined',
  teacher_joined: 'Teacher joined',
  student_absent: 'Student not joined',
  teacher_absent: 'Teacher not joined',
  login: 'Sign-ins',
  feedback_overdue: 'Feedback overdue',
  renewal_needed: 'Renewals',
  time_mismatch: 'Time zone issues',
  certificates_needed: 'Certificates',
  parent_feedback: 'Parent feedback',
  enroll_interest: 'Enroll interest',
  demos_on: 'Demos turned on',
  demos_off: 'Demos turned off',
  no_demo_slots: 'No demo slots',
  pause_ended: 'Pause ended',
  student_paused: 'Student paused',
  student_stopped: 'Student stopped',
  student_resumed: 'Student back',
  referral_trial: 'Referral: free class',
  referral_paid: 'Referral: discount ready',
  competition_vote: 'Competition votes',
  competition_join: 'Challenge: joined',
  competition_entry: 'Challenge: project sent',
  badge_earned: 'Badges',
  badge_nomination: 'Badge nominations',
  photo_pending: 'Photos to approve',
  teacher_profile_pending: 'Teacher cards to approve',
  pending_ending: 'Pending: ends soon',
  pending_expired: 'Pending: slots freed',
  enroll_request: 'Enrolled from the link',
  feedback_open: 'Feedback link opened',
  winback_open: 'Win-back link opened',
  pending_no_times: 'Pending: no times yet',
  next_course: 'Next course chosen',
  session_type: 'Mastery not paid',
  question_reported: 'Reported messages',
};
const typeLabel = (t) => TYPE_LABELS[t] || String(t || '').replace(/_/g, ' ');

// times are saved with GETUTCDATE(), so new Date(value) is right
const dayKey = (v) => new Date(v).toLocaleDateString('en-CA', { timeZone: TZ });
const dayTitle = (key) => {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: TZ });
  const y = new Date(Date.now() - 86400000).toLocaleDateString('en-CA', { timeZone: TZ });
  if (key === today) return 'Today';
  if (key === y) return 'Yesterday';
  const [yy, mm, dd] = key.split('-').map(Number);
  return new Date(Date.UTC(yy, mm - 1, dd)).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};
const clock = (v) => new Date(v).toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });

const NotificationsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('userData');

  const [group, setGroup] = useState('alerts');
  const [type, setType] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState('');

  const call = useCallback(
    async (path, options = {}) => {
      const res = await fetch(`${BASE}${path}`, { ...options, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    [token]
  );

  const query = useCallback(
    (offset) => {
      const p = new URLSearchParams({ group, offset: String(offset), limit: String(PAGE) });
      if (type) p.set('type', type);
      if (unreadOnly) p.set('unread', '1');
      if (q) p.set('q', q);
      return `/page?${p.toString()}`;
    },
    [group, type, unreadOnly, q]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await call(query(0));
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(data.total || 0);
      setTypes(Array.isArray(data.types) ? data.types : []);
    } catch {
      setError('Couldn’t load the notifications.');
    } finally {
      setLoading(false);
    }
  }, [call, query]);

  useEffect(() => {
    load();
  }, [load]);

  // search after the admin stops typing
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadMore = async () => {
    setMore(true);
    try {
      const data = await call(query(items.length));
      setItems((list) => [...list, ...(data.items || [])]);
      setTotal(data.total || 0);
    } catch {
      setError('Couldn’t load more.');
    } finally {
      setMore(false);
    }
  };

  const open = (n) => {
    if (!n.IsRead) {
      setItems((list) => list.map((x) => (x.Id === n.Id ? { ...x, IsRead: true } : x)));
      call(`/${n.Id}/read`, { method: 'PUT' }).catch(() => {});
    }
    if (n.Link) navigate(n.Link);
  };

  const readAll = async () => {
    setItems((list) => list.map((x) => ({ ...x, IsRead: true })));
    try {
      await call(`/read-all?group=${group}`, { method: 'PUT' });
      load();
    } catch {
      load();
    }
  };

  // types in this tab (sign-ins live in their own tab)
  const typeOptions = useMemo(
    () =>
      types
        .filter((t) => (group === 'activity' ? t.Type === 'login' : group === 'alerts' ? t.Type !== 'login' : true))
        .sort((a, b) => typeLabel(a.Type).localeCompare(typeLabel(b.Type))),
    [types, group]
  );
  const unreadHere = typeOptions.reduce((n, t) => n + (t.unread || 0), 0);

  const days = useMemo(() => {
    const out = [];
    items.forEach((n) => {
      const k = dayKey(n.CreatedAt);
      const last = out[out.length - 1];
      if (last && last.key === k) last.items.push(n);
      else out.push({ key: k, items: [n] });
    });
    return out;
  }, [items]);

  return (
    <div className="rmnp">
      <header className="rmnp-head">
        <div>
          <h1>Notifications</h1>
          <p>
            {total} {total === 1 ? 'notification' : 'notifications'}
            {unreadHere > 0 && <> · <b>{unreadHere} unread</b></>}
          </p>
        </div>
        <div className="rmnp-actions">
          <button type="button" className="rmnp-btn" onClick={load} aria-label="Refresh">
            <FontAwesomeIcon icon={faRotateRight} />
          </button>
          <button type="button" className="rmnp-btn rmnp-btn--main" onClick={readAll} disabled={!unreadHere}>
            <FontAwesomeIcon icon={faCheckDouble} /> Mark all read
          </button>
        </div>
      </header>

      <div className="rmnp-bar">
        <div className="rmnp-tabs" role="tablist">
          {[
            ['alerts', 'Alerts'],
            ['activity', 'Sign-ins'],
            ['all', 'Everything'],
          ].map(([k, l]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={group === k}
              className={group === k ? 'is-on' : ''}
              onClick={() => {
                setGroup(k);
                setType('');
              }}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="rmnp-search">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <input type="search" placeholder="Search a name or a word" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="rmnp-select" value={type} onChange={(e) => setType(e.target.value)} aria-label="Type">
          <option value="">All types</option>
          {typeOptions.map((t) => (
            <option key={t.Type} value={t.Type}>
              {typeLabel(t.Type)} ({t.n})
            </option>
          ))}
        </select>
        <label className="rmnp-check">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
          Unread only
        </label>
      </div>

      {loading ? (
        <div className="rmnp-skel" />
      ) : error && items.length === 0 ? (
        <p className="rmnp-empty is-error">{error}</p>
      ) : items.length === 0 ? (
        <div className="rmnp-empty">
          <FontAwesomeIcon icon={faBell} />
          <p>Nothing here.</p>
        </div>
      ) : (
        <>
          {days.map((d) => (
            <section key={d.key} className="rmnp-day">
              <h2>{dayTitle(d.key)}</h2>
              <ul>
                {d.items.map((n) => {
                  const meta = TYPE_META[n.Type] || { icon: faBell, tone: 'blue' };
                  return (
                    <li key={n.Id}>
                      <button type="button" className={`rmnp-item ${n.IsRead ? '' : 'is-unread'} ${n.Link ? 'has-link' : ''}`} onClick={() => open(n)}>
                        <span className={`rmnp-icon tone-${meta.tone}`} aria-hidden="true">
                          <FontAwesomeIcon icon={meta.icon} />
                        </span>
                        <span className="rmnp-text">
                          <span className="rmnp-title">{n.Title}</span>
                          {n.Message && <span className="rmnp-msg">{n.Message}</span>}
                          <span className="rmnp-meta">
                            <span className="rmnp-type">{typeLabel(n.Type)}</span>
                            <span title={new Date(n.CreatedAt).toLocaleString('en-US', { timeZone: TZ })}>
                              {clock(n.CreatedAt)} · {ago(n.CreatedAt)}
                            </span>
                          </span>
                        </span>
                        {!n.IsRead && <span className="rmnp-dot" aria-label="Unread" />}
                        {n.Link && <FontAwesomeIcon icon={faArrowRight} className="rmnp-go" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          {items.length < total && (
            <div className="rmnp-more">
              <button type="button" className="rmnp-btn" onClick={loadMore} disabled={more}>
                {more ? 'Loading…' : `Load more (${total - items.length} left)`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationsPage;
